import { formatPropertyUnitLabel } from "../../../../../../../../_shared/_logic/unit-labels"
import type { StatusDefinition } from "@kontextwerk/web-sdk/core/display/status"

import type { CaseCommunicationLetterDelivery } from "../../../../../../../../api/kehrblatt/case-communications"
import {
  buildRichTooltip,
  tooltipField,
  tooltipText,
  tooltipTitle,
} from "../../../../../../../../_shared/_logic/rich-tooltips"
import type { ContactOrderPlanHint } from "../../../_logic/communication-plan-table-contract"
import {
  channelIcon,
  formatCompactPostalAddress,
  type RecipientTagPresentation,
} from "./recipientPresentation"

type LetterDeliveryStatusPresentation = {
  definition: StatusDefinition
  label: string
  subLabel: string | null
  tooltip: string
  tone: string
}

export type HistoricalLetterDeliveryPresentation = {
  recipient: RecipientTagPresentation
  hint: ContactOrderPlanHint
  status: LetterDeliveryStatusPresentation | null
}

const text = (value: string | null | undefined) => value?.trim() || null

const firstText = (...values: Array<string | null | undefined>) => {
  for (const value of values) {
    const normalized = text(value)

    if (normalized) return normalized
  }

  return null
}

const propertySummary = (delivery: CaseCommunicationLetterDelivery) => {
  const property = delivery.property
  const target = delivery.target
  const base = firstText(
    property?.display_name,
    property?.address,
    target?.display_name,
    target?.address,
  )
  const unit = property?.unit_label ? formatPropertyUnitLabel(property.unit_label) : ""

  return [base, unit].filter(Boolean).join(" · ") || null
}

const targetSummary = (delivery: CaseCommunicationLetterDelivery) =>
  firstText(delivery.target?.address, delivery.target?.display_name)

const recipientPresentation = (
  delivery: CaseCommunicationLetterDelivery | null | undefined,
): RecipientTagPresentation => {
  if (!delivery || delivery.source === "undocumented") {
    return {
      label: "Ziel nicht dokumentiert",
      subLabel: "Historischer Brief",
      nameLabel: null,
      targetLabel: null,
      icon: channelIcon("LETTER"),
      color: "orange",
      isUsable: true,
      tooltipHtml: buildRichTooltip([
        tooltipTitle("Empfänger"),
        tooltipText("Die damalige Zustelladresse ist nicht dokumentiert."),
      ]),
    }
  }

  const isNotice = delivery.print_mode === "GENERIC_KLEBEZETTEL"
  const recipientName = isNotice
    ? firstText(delivery.property?.display_name, delivery.target?.display_name)
    : text(delivery.target?.display_name)
  const address = isNotice
    ? firstText(delivery.property?.address, delivery.target?.address)
    : text(delivery.target?.address)
  const visibleAddress = isNotice ? address : formatCompactPostalAddress(address)
  const unit =
    isNotice && delivery.property?.unit_label
      ? formatPropertyUnitLabel(delivery.property.unit_label)
      : null

  const targetLabel = [visibleAddress, unit].filter(Boolean).join(" · ") || null
  const label = recipientName || targetLabel

  if (!label) return recipientPresentation(null)

  const subLabelParts = [
    visibleAddress && visibleAddress !== recipientName ? visibleAddress : null,
    unit,
  ].filter(Boolean)

  return {
    label,
    subLabel: subLabelParts.join(" · ") || (isNotice ? "Aushang" : "Terminbrief"),
    nameLabel: recipientName,
    targetLabel,
    icon: channelIcon("LETTER"),
    color: "emerald",
    isUsable: true,
    tooltipHtml: "",
  }
}

const documentedSummary = (delivery: CaseCommunicationLetterDelivery | null | undefined) => {
  if (!delivery || delivery.source === "undocumented") return null

  return delivery.print_mode === "GENERIC_KLEBEZETTEL"
    ? propertySummary(delivery)
    : targetSummary(delivery)
}

const manualStatusPresentation = ({
  baseDefinition,
  baseSubLabel,
  isNotice,
}: {
  baseDefinition: StatusDefinition
  baseSubLabel: string | null
  isNotice: boolean
}): LetterDeliveryStatusPresentation => ({
  definition: {
    ...baseDefinition,
    value: "CREATED",
    label: "Erstellt",
    color: "emerald",
  },
  label: "Erstellt",
  subLabel: baseSubLabel?.replace(/^Gesendet am /, "Erstellt am ") ?? null,
  tooltip: isNotice
    ? "Der Aushang wurde erstellt. Ob er angebracht wurde, ist damit noch nicht bestätigt."
    : "Der Brief wurde erstellt. Die Zustellung wurde noch nicht bestätigt.",
  tone: "success",
})

export const buildHistoricalLetterDeliveryPresentation = ({
  delivery,
  communicationStatus,
  baseHint,
  baseStatusDefinition,
  baseStatusSubLabel,
}: {
  delivery: CaseCommunicationLetterDelivery | null | undefined
  communicationStatus: string
  baseHint: ContactOrderPlanHint
  baseStatusDefinition: StatusDefinition
  baseStatusSubLabel: string | null
}): HistoricalLetterDeliveryPresentation => {
  const recipient = recipientPresentation(delivery)
  const summary = documentedSummary(delivery)

  if (!delivery || delivery.source === "undocumented" || !summary) {
    return {
      recipient,
      hint: {
        ...baseHint,
        subLabel: "Zieladresse nicht dokumentiert",
        tooltipHtml: buildRichTooltip([
          tooltipTitle(baseHint.label),
          tooltipText("Die damalige Zustelladresse ist nicht dokumentiert."),
        ]),
      },
      status: null,
    }
  }

  const isNotice = delivery.print_mode === "GENERIC_KLEBEZETTEL"
  const isSent = communicationStatus === "SENT" || communicationStatus === "DELIVERED"

  if (isNotice) {
    if (!isSent) {
      return {
        recipient,
        hint: {
          ...baseHint,
          subLabel: `Aushang für ${summary}`,
          tooltipHtml: "",
        },
        status: null,
      }
    }

    return {
      recipient,
      hint: {
        ...baseHint,
        label: "Aushang erstellt",
        subLabel: `Aushang für ${summary}`,
        color: "emerald",
        tooltipHtml: "",
      },
      status: manualStatusPresentation({
        baseDefinition: baseStatusDefinition,
        baseSubLabel: baseStatusSubLabel,
        isNotice: true,
      }),
    }
  }

  if (delivery.delivery_mode === "manual") {
    if (!isSent) {
      return {
        recipient,
        hint: {
          ...baseHint,
          subLabel: `Vorgesehen für ${summary}`,
          tooltipHtml: "",
        },
        status: null,
      }
    }

    return {
      recipient,
      hint: {
        ...baseHint,
        label: "Terminbrief erstellt",
        subLabel: `Ausgestellt für ${summary}`,
        color: "emerald",
        tooltipHtml: "",
      },
      status: manualStatusPresentation({
        baseDefinition: baseStatusDefinition,
        baseSubLabel: baseStatusSubLabel,
        isNotice: false,
      }),
    }
  }

  const label =
    isSent && delivery.delivery_mode === "automatic" ? "Brief versendet" : baseHint.label
  const destinationPrefix = isSent ? "Versendet an" : "Vorgesehen für"

  return {
    recipient,
    hint: {
      ...baseHint,
      label,
      subLabel: `${destinationPrefix} ${summary}`,
      color: isSent ? "emerald" : baseHint.color,
      tooltipHtml: baseHint.tooltipHtml ?? "",
    },
    status: null,
  }
}
